import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getEngagement } from "@/lib/admin/concierge-insights-api";

function fmtDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}
function fmtDuration(sec: number) {
  if (!sec) return "0m";
  const m = Math.round(sec / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function labelFor(item: { event_type: string; resource_title: string | null; metadata: any }) {
  switch (item.event_type) {
    case "page_view":
      return "Opened the concierge page";
    case "resource_view":
      return `Opened ${item.resource_title ?? "a resource"}`;
    case "resource_complete":
      return `Finished ${item.resource_title ?? "a resource"}`;
    case "session_start":
      return "Started a voice session";
    case "session_end": {
      const ms = Number(item.metadata?.duration_ms ?? 0);
      const mins = Math.max(1, Math.round(ms / 60000));
      return `Talked with the advisor (${mins}m)`;
    }
    default:
      return item.event_type;
  }
}

export default function ConciergeInsights() {
  const { siteId = "" } = useParams();
  const { data, isLoading, error } = useQuery({
    queryKey: ["concierge", "admin", "insights", siteId],
    queryFn: () => getEngagement(siteId),
    enabled: !!siteId,
    staleTime: 30_000,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin opacity-60" />
      </div>
    );
  }
  if (error) {
    return <p className="text-sm text-destructive">Couldn't load engagement.</p>;
  }
  if (!data) return null;

  const s = data.summary;
  const consentLabel =
    s.consent_state === "active"
      ? "Consent active"
      : s.consent_state === "withdrawn"
        ? "Consent withdrawn"
        : "No consent yet";

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <header className="flex items-center gap-3">
        <Button asChild variant="ghost" size="icon">
          <Link to={`/app/admin/concierge/${siteId}`} aria-label="Back">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-xl font-heading font-semibold">
            {data.site.title || "Concierge"} — engagement
          </h1>
          <p className="text-xs text-muted-foreground">{consentLabel}</p>
        </div>
      </header>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Kpi label="Last active" value={fmtDate(s.last_active_at)} />
        <Kpi label="Voice sessions" value={String(s.session_count)} />
        <Kpi label="Voice time" value={fmtDuration(s.total_voice_seconds)} />
        <Kpi label="Resources opened" value={String(s.resource_views)} />
        <Kpi label="Resources finished" value={String(s.resource_completes)} />
      </div>

      <Card>
        <CardContent className="p-6 space-y-4">
          <h2 className="text-lg font-heading font-semibold">Recent activity</h2>
          {data.timeline.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {s.consent_state === "none"
                ? "Waiting for the user to give consent. Nothing to show yet."
                : "You haven't seen any activity yet."}
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {data.timeline.map((it, i) => (
                <li key={i} className="py-2 flex justify-between gap-3 text-sm">
                  <span>{labelFor(it)}</span>
                  <span className="text-muted-foreground shrink-0">{fmtDate(it.created_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-lg font-medium mt-1">{value}</p>
      </CardContent>
    </Card>
  );
}
